import { z } from "zod/v4-mini";

import { type PumpDeviceBrand, pumpDeviceBrandSchema } from "@/models/MedicalDeviceModel.ts";
import { Schema, cz } from "@/utils/zodUtils.ts";

import { TelemonitoringTier, telemonitoringTierSchema } from "./CarePlanModel";
import { ConditionType, MedicationSchema, conditionTypeSchema, medicationSchemeSchema } from "./ConditionModel";
import { FileModel, fileSchema } from "./FileModel";
import { TeleMonitoringCriteria, telemonitoringCriteriaSchema } from "./TelemonitoringCriteriaModel";

export type CarePlanCreationDirectives = {
  periodStart: string;
};

type TelemonitoringCriteriaMapMedication = {
  [key in MedicationSchema]: TeleMonitoringCriteria[];
};

export type TelemonitoringCriteriaMap = {
  [key in ConditionType]: TelemonitoringCriteriaMapMedication;
};

export type TelemonitoringTierMap = {
  [key in TeleMonitoringCriteria]: TelemonitoringTier;
};

type TelemonitoringNurse = {
  id: string;
  firstName: string;
  lastName: string;
  profilePicture?: FileModel;
};

type PreviousCarePlanData = {
  telemonitoringCriteria?: TeleMonitoringCriteria;
  description?: string;
  descriptionAttachment?: FileModel;
  treatmentPrescriptionAttachment?: FileModel;
  referralLetterAttachment?: FileModel;
};

type ConditionData = {
  diabetesType: ConditionType;
  insulinType?: MedicationSchema;
  termDate?: string;
  pumpModel?: PumpDeviceBrand;
};

export type CarePlanDirectives = {
  previousCarePlanData?: PreviousCarePlanData; // Used to pre-fill the renewal form
  previousConditionData?: ConditionData; // Used to pre-fill the renewal form
  carePlanCreationDataDirectives: CarePlanCreationDirectives; // Used to pass data computed by the backend needed to be known by the practitioner (periodStart for example)
  telemonitoringCriteriaMap: TelemonitoringCriteriaMap; // Gives the criteras available for each condition and medication
  telemonitoringTierMap: TelemonitoringTierMap; // For each criteria, gives the tier
  allowedInsulinScheme: MedicationSchema[]; // The medication schemas allowed for the patient
  allowedDiabetesTypes: ConditionType[]; // The diabetes types allowed for the patient
  restrictions?: TelemonitoringTier;
  telemonitoringNurses: TelemonitoringNurse[];
};

export type PatientCreationDirectives = {
  telemonitoringCriteriaMap: TelemonitoringCriteriaMap; // Gives the criteras available for each condition and medication
  telemonitoringTierMap: TelemonitoringTierMap; // For each criteria, gives the tier
  carePlanCreationDataDirectives: CarePlanCreationDirectives; // Used to pass data computed by the backend needed to be known by the practitioner (periodStart for example)
  allowedInsulinScheme: MedicationSchema[]; // The medication schemas allowed for the patient
  allowedDiabetesTypes: ConditionType[]; // The diabetes types allowed for the patient
  restrictions?: TelemonitoringTier;
  telemonitoringNurses: TelemonitoringNurse[];
};

const conditionDataSchema = cz.camelCaseObject({
  diabetesType: conditionTypeSchema,
  insulinType: cz.optional(medicationSchemeSchema),
  termDate: cz.optional(z.string()),
  pump_model: cz.optional(pumpDeviceBrandSchema),
});

const previousCarePlanScheme = z.object({
  telemonitoringCriteria: cz.optional(telemonitoringCriteriaSchema),
  description: cz.optional(z.string()),
  descriptionAttachment: cz.optional(fileSchema),
  treatmentPrescriptionAttachment: cz.optional(fileSchema),
  referralLetterAttachment: cz.optional(fileSchema),
});

const telemonitoringNurseSchema = cz.camelCaseObject({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  profile_picture: cz.optional(fileSchema),
});

const telemonitoringCriteriaMapMedicationSchema: Schema<TelemonitoringCriteriaMapMedication> = z.record(
  medicationSchemeSchema,
  cz.forcedArray(telemonitoringCriteriaSchema),
);

const telemonitoringCriteriaMapSchema: Schema<TelemonitoringCriteriaMap> = z.record(
  conditionTypeSchema,
  telemonitoringCriteriaMapMedicationSchema,
);

export const carePlanDirectivesSchema: Schema<CarePlanDirectives> = cz.transform(
  z.object({
    previousCarePlanData: cz.optional(previousCarePlanScheme),
    previousConditionData: cz.optional(conditionDataSchema),
    carePlanCreationDataDirectives: z.object({
      periodStart: z.string(),
    }),
    telemonitoringCriteriaMap: telemonitoringCriteriaMapSchema,
    telemonitoringTierMap: z.record(telemonitoringCriteriaSchema, telemonitoringTierSchema),
    allowedInsulinScheme: cz.forcedArray(medicationSchemeSchema),
    allowedDiabetesTypes: cz.forcedArray(conditionTypeSchema),
    restrictions: cz.optional(telemonitoringTierSchema),
    telemonitoring_nurses: cz.optional(cz.forcedArray(telemonitoringNurseSchema)),
  }),
  data => ({
    ...data,
    telemonitoringNurses: data.telemonitoring_nurses ?? [],
  }),
);

export const patientCreationDirectivesSchema: Schema<PatientCreationDirectives> = cz.transform(
  z.object({
    telemonitoringCriteriaMap: telemonitoringCriteriaMapSchema,
    telemonitoringTierMap: z.record(telemonitoringCriteriaSchema, telemonitoringTierSchema),
    allowedInsulinScheme: cz.forcedArray(medicationSchemeSchema),
    carePlanCreationDataDirectives: z.object({
      periodStart: z.string(),
    }),
    allowedDiabetesTypes: cz.forcedArray(conditionTypeSchema),
    restrictions: cz.optional(telemonitoringTierSchema),
    telemonitoring_nurses: cz.optional(cz.forcedArray(telemonitoringNurseSchema)),
  }),
  data => ({
    ...data,
    telemonitoringNurses: data.telemonitoring_nurses ?? [],
  }),
);
